<template>
	<div class="bg-[#fefefe] h-full">
		<div class="flex justify-center w-full">
			<BreadCrumb
				v-model="step"
				:crumbs="steps"
			/>
		</div>
		<div class="px-14 flex flex-col gap-4">
			<div>
				<h1 class="text-2xl font-bold color-[#272728]">
					{{ $t('mapProperties') }}
				</h1>
				<p>{{ $t('cliqueCelulaParaEditar') }}</p>
			</div>
			<div>
				<div class="flex gap-4 justify-center w-full">
					<InfoCard
						v-if="store.getters.VTCC_MENOR_VTSC.length"
						action="filtrar"
						:description="$t('VTCCMenorQueVTSC')"
						type="error"
						@action="filterIds = store.getters.VTCC_MENOR_VTSC.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.VICC_MENOR_VISC.length"
						action="filtrar"
						:description="$t('VICCMenorQueVISC')"
						type="error"
						@action="filterIds = store.getters.VICC_MENOR_VISC.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.DAP_INCONSISTENTE.length"
						action="filtrar"
						:description="$t('DAPInconsistente')"
						type="error"
						@action="filterIds = store.getters.DAP_INCONSISTENTE.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.HT_INCONSISTENTE.length"
						action="filtrar"
						:description="$t('HTInconsistente')"
						type="error"
						@action="filterIds = store.getters.HT_INCONSISTENTE.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.DG_MENOR_DAP_MED.length"
						action="filtrar"
						:description="$t('DGMenorQueDAPMED')"
						type="error"
						@action="filterIds = store.getters.DG_MENOR_DAP_MED.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.HDOM_MENOR_HMED.length"
						action="filtrar"
						:description="$t('HDOMMenorQueHMED')"
						type="error"
						@action="filterIds = store.getters.HDOM_MENOR_HMED.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.SIGLA_INVENTARIO.length"
						action="filtrar"
						:description="$t('rotuloTipoInventario')"
						type="error"
						@action="filterIds = store.getters.SIGLA_INVENTARIO.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.SEM_MATERIAL_GENETICO.length"
						action="filtrar"
						:description="$t('materialGeneticoNaoCadastrado')"
						type="error"
						@action="filterIds = store.getters.SEM_MATERIAL_GENETICO.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.SEM_ESPECIE.length"
						action="filtrar"
						:description="$t('especieNaoCadastrada')"
						type="error"
						@action="filterIds = store.getters.SEM_ESPECIE.map((m) => m.id)"
					/>

					<InfoCard
						v-if="store.getters.OBRIGATORIOS_NAO_MAPEADOS.length"
						:details="
							store.getters.OBRIGATORIOS_NAO_MAPEADOS.map(
								(coluna) => `Coluna ${coluna.nome} deve ser mapeada.`,
							)
						"
						:description="$t('mapeamentoObrigatorio')"
						type="error"
					/>

					<InfoCard
						v-if="store.getters.OBRIGATORIOS_PROCESSAMENTO"
						:details="['Ao menos uma coluna relacionada a dados de processameto deve ser mapeada']"
						:description="$t('mapeamentoObrigatorioProcessamento')"
						type="error"
					/>
				</div>

				<NewDataMapper
					v-model="$store.state.moduleDadosHistoricos.colunaAtributo"
					:linhas-inconsistentes="store.getters.linhasInconsistentes"
					:filter-ids="filterIds"
					:filename="$store.state.moduleDadosHistoricos.file[0].name || ''"
					:data="$store.state.moduleDadosHistoricos.medicoesHistoricas"
					:colunas="store.getters.colunasMapeamento.concat(store.getters.colunasProcessamento)"
					@cellChanged="handleCellChanged"
					@limparFiltros="filterIds = []"
					@change="$store.dispatch('moduleDadosHistoricos/handleFileUpload', [$event])"
					@close="
						enableFileUpload = false
						showForm = true
					"
				/>
			</div>

			<div class="flex justify-end">
				<Button
					:disabled="!ableToContinue"
					class="w-fit"
					width-auto
					:label="$t('continue')"
					@click="$router.push('visualizarDispersao')"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import BreadCrumb from '../../../components/BreadCrumb/BreadCrumb.vue'
import Button from '../../../components/Button/Button.vue'
import InfoCard from '../../../components/InfoCard/InfoCard.vue'
import NewDataMapper from '../../../components/NewDataMapper/NewDataMapper.vue'
import { useDadosHistoricosStore } from '../../../store/modules/moduleDadosHistoricos.js'
import i18n from '../../../VueI18n.js'

// Helper para acessar as traduções no Composition API
const $t = (key) => i18n.t(key)

const store = useDadosHistoricosStore()

const filterIds = ref([])

function handleCellChanged(value) {
	store.commit('changeRow', value)
}

const steps = ref([
	{
		name: $t('cadastrarBaseHistorica'),
		step: 0,
	},
	{
		name: $t('mapeamentoDeColunas'),
		step: 1,
	},
	{
		name: $t('visualizeDispersion'),
		step: 2,
	},
])

const step = ref(1)

const ableToContinue = computed(() => {
	return (
		!store.getters.OBRIGATORIOS_PROCESSAMENTO &&
		!store.getters.OBRIGATORIOS_NAO_MAPEADOS.length &&
		!store.getters.SEM_ESPECIE.length &&
		!store.getters.SEM_MATERIAL_GENETICO.length &&
		!store.getters.SIGLA_INVENTARIO.length &&
		!store.getters.HDOM_MENOR_HMED.length &&
		!store.getters.DG_MENOR_DAP_MED.length &&
		!store.getters.HT_INCONSISTENTE.length &&
		!store.getters.DAP_INCONSISTENTE.length &&
		!store.getters.VICC_MENOR_VISC.length &&
		!store.getters.VTCC_MENOR_VTSC.length &&
		!store.getters.linhasInconsistentes.length
	)
})

onMounted(() => {
	store.commit('setEspecies')
	store.commit('setMateriaisGeneticos')
})
</script>

<style></style>
